// Social Wall Post Templates Configuration

export interface TemplateField {
  id: string;
  label: string;
  emoji: string;
  type: 'text' | 'video' | 'image' | 'textarea' | 'poll_options';
  placeholder: string;
  required: boolean;
}

export interface PostTemplate {
  id: string;
  name: string;
  description: string;
  fields: TemplateField[];
  displayEmojis: string[];
  category: 'game_day' | 'training' | 'growth' | 'community';
}

export const TEMPLATE_CATEGORIES = {
  game_day: { label: 'Game Day', emoji: '🏆' },
  training: { label: 'Training', emoji: '💪' },
  growth: { label: 'Growth', emoji: '📈' },
  community: { label: 'Community', emoji: '🤝' },
};

export const WALL_POST_TEMPLATES: PostTemplate[] = [
  // Game Day Templates
  {
    id: 'one_clip_three_truths',
    name: 'One Clip, Three Truths',
    description: 'Share a clip with what went right, what you learned, and your next goal',
    displayEmojis: ['🎬', '✅', '🧠', '🎯'],
    category: 'game_day',
    fields: [
      {
        id: 'clip',
        label: 'Clip',
        emoji: '🎬',
        type: 'video',
        placeholder: 'Upload your video clip',
        required: true,
      },
      {
        id: 'what_went_right',
        label: 'What went right',
        emoji: '✅',
        type: 'textarea',
        placeholder: 'What did you do well in this play?',
        required: true,
      },
      {
        id: 'what_i_learned',
        label: 'What I learned',
        emoji: '🧠',
        type: 'textarea',
        placeholder: 'What did this teach you?',
        required: true,
      },
      {
        id: 'next_rep_goal',
        label: 'Next rep goal',
        emoji: '🎯',
        type: 'textarea',
        placeholder: 'What will you do differently next time?',
        required: true,
      },
    ],
  },
  {
    id: 'pressure_moment',
    name: 'Pressure Moment',
    description: 'Break down a high-pressure situation and how you handled it',
    displayEmojis: ['⏱️', '😤', '🔥', '🧊'],
    category: 'game_day',
    fields: [
      {
        id: 'situation',
        label: 'Situation',
        emoji: '⏱️',
        type: 'textarea',
        placeholder: 'Describe the pressure moment',
        required: true,
      },
      {
        id: 'what_i_was_thinking',
        label: 'What I was thinking',
        emoji: '😤',
        type: 'textarea',
        placeholder: 'What was going through your mind?',
        required: true,
      },
      {
        id: 'what_happened',
        label: 'What happened',
        emoji: '🔥',
        type: 'textarea',
        placeholder: 'How did it play out?',
        required: true,
      },
      {
        id: 'what_id_do_again',
        label: "What I'd do again",
        emoji: '🧊',
        type: 'textarea',
        placeholder: 'What would you repeat or change?',
        required: true,
      },
    ],
  },
  {
    id: 'game_day_snapshot',
    name: 'Game-Day Snapshot',
    description: "Capture your mindset and mission before the game",
    displayEmojis: ['📸', '😊', '🎯', '⚔️'],
    category: 'game_day',
    fields: [
      {
        id: 'photo',
        label: 'Photo',
        emoji: '📸',
        type: 'image',
        placeholder: 'Upload a game-day photo',
        required: true,
      },
      {
        id: 'mood',
        label: 'Mood',
        emoji: '😊',
        type: 'text',
        placeholder: 'How are you feeling?',
        required: true,
      },
      {
        id: 'mission',
        label: 'Mission',
        emoji: '🎯',
        type: 'text',
        placeholder: "What's your goal today?",
        required: true,
      },
      {
        id: 'matchup',
        label: 'Matchup',
        emoji: '⚔️',
        type: 'text',
        placeholder: 'Who are you facing?',
        required: true,
      },
    ],
  },

  // Training Templates
  {
    id: 'hustle_over_highlight',
    name: 'Hustle > Highlight',
    description: "Show the work that doesn't make the highlight reel",
    displayEmojis: ['🎬', '📌', '🧱', '💪'],
    category: 'training',
    fields: [
      {
        id: 'clip',
        label: 'Clip',
        emoji: '🎬',
        type: 'video',
        placeholder: 'Upload your hustle clip',
        required: true,
      },
      {
        id: 'my_job',
        label: 'My job',
        emoji: '📌',
        type: 'textarea',
        placeholder: 'What was your role in this play?',
        required: true,
      },
      {
        id: 'why_it_matters',
        label: 'Why it matters',
        emoji: '🧱',
        type: 'textarea',
        placeholder: 'Why is this effort important?',
        required: true,
      },
    ],
  },
  {
    id: 'mini_combine_check',
    name: 'Mini Combine Check',
    description: 'Track your weekly training progress',
    displayEmojis: ['🏃', '🎬', '📈', '🎯'],
    category: 'training',
    fields: [
      {
        id: 'drill_name',
        label: 'Drill Name',
        emoji: '🏃',
        type: 'text',
        placeholder: 'What drill are you working on?',
        required: true,
      },
      {
        id: 'clip',
        label: 'Clip',
        emoji: '🎬',
        type: 'video',
        placeholder: 'Upload your drill clip',
        required: true,
      },
      {
        id: 'this_weeks_number',
        label: "This Week's Number",
        emoji: '📈',
        type: 'text',
        placeholder: 'Your current performance (time, reps, etc.)',
        required: true,
      },
      {
        id: 'next_target',
        label: 'Next Target',
        emoji: '🎯',
        type: 'text',
        placeholder: "What's your next goal?",
        required: true,
      },
    ],
  },
  {
    id: 'coach_cam',
    name: 'Coach Cam',
    description: 'Share what you learned from your coach',
    displayEmojis: ['🧠', '🎬', '✅'],
    category: 'training',
    fields: [
      {
        id: 'coaching_point',
        label: 'Coaching Point',
        emoji: '🧠',
        type: 'textarea',
        placeholder: 'What did your coach teach you?',
        required: true,
      },
      {
        id: 'clip',
        label: 'Clip',
        emoji: '🎬',
        type: 'video',
        placeholder: 'Upload the clip showing the lesson',
        required: true,
      },
      {
        id: 'results',
        label: 'Results',
        emoji: '✅',
        type: 'textarea',
        placeholder: 'How did it work out?',
        required: true,
      },
    ],
  },

  // Growth Templates
  {
    id: 'glow_up_series',
    name: 'Glow-Up Series',
    description: 'Show your improvement over time',
    displayEmojis: ['📅', '🔧', '🧠'],
    category: 'growth',
    fields: [
      {
        id: 'then_clip',
        label: 'Then Clip',
        emoji: '📅',
        type: 'video',
        placeholder: 'Upload your old clip (before)',
        required: true,
      },
      {
        id: 'now_clip',
        label: 'Now Clip',
        emoji: '📅',
        type: 'video',
        placeholder: 'Upload your new clip (after)',
        required: true,
      },
      {
        id: 'what_changed',
        label: 'What Changed',
        emoji: '🔧',
        type: 'textarea',
        placeholder: 'What did you work on to improve?',
        required: true,
      },
      {
        id: 'what_surprised_me',
        label: 'What Surprised Me',
        emoji: '🧠',
        type: 'textarea',
        placeholder: 'What was unexpected about your progress?',
        required: true,
      },
    ],
  },
  {
    id: 'reset_day',
    name: 'Reset Day',
    description: 'Share setbacks and comebacks',
    displayEmojis: ['😖', '🧠', '🔥'],
    category: 'growth',
    fields: [
      {
        id: 'what_didnt_go_well',
        label: "What Didn't Go Well",
        emoji: '😖',
        type: 'textarea',
        placeholder: 'What happened that was tough?',
        required: true,
      },
      {
        id: 'what_i_learned',
        label: 'What I Learned',
        emoji: '🧠',
        type: 'textarea',
        placeholder: 'What did this teach you?',
        required: true,
      },
      {
        id: 'what_im_doing_next',
        label: "What I'm Doing Next",
        emoji: '🔥',
        type: 'textarea',
        placeholder: 'How will you bounce back?',
        required: true,
      },
    ],
  },

  // Community Templates
  {
    id: 'teammate_assist_credit',
    name: 'Teammate Assist Credit',
    description: 'Give props to a teammate who helped you succeed',
    displayEmojis: ['🎬', '🤝', '🗣️'],
    category: 'community',
    fields: [
      {
        id: 'clip',
        label: 'Clip',
        emoji: '🎬',
        type: 'video',
        placeholder: 'Upload a clip showing the assist',
        required: true,
      },
      {
        id: 'teammate_mention',
        label: 'Teammate',
        emoji: '🤝',
        type: 'text',
        placeholder: 'Who helped you? (name or @mention)',
        required: true,
      },
      {
        id: 'thank_you',
        label: 'Thank you',
        emoji: '🗣️',
        type: 'textarea',
        placeholder: 'Why are you grateful for this teammate?',
        required: true,
      },
    ],
  },
  {
    id: 'quick_poll_clip',
    name: 'Quick Poll + Clip',
    description: 'Ask your followers for input',
    displayEmojis: ['🎬', '🗳️'],
    category: 'community',
    fields: [
      {
        id: 'clip',
        label: 'Clip',
        emoji: '🎬',
        type: 'video',
        placeholder: 'Upload your video clip',
        required: true,
      },
      {
        id: 'poll_question',
        label: 'Poll Question',
        emoji: '🗳️',
        type: 'text',
        placeholder: 'What do you want to ask?',
        required: true,
      },
      {
        id: 'poll_options',
        label: 'Poll Options',
        emoji: '🗳️',
        type: 'poll_options',
        placeholder: 'Add 2-4 options',
        required: true,
      },
    ],
  },
];

export const getTemplateById = (id: string): PostTemplate | undefined => {
  return WALL_POST_TEMPLATES.find((t) => t.id === id);
};

export const getTemplatesByCategory = (category: PostTemplate['category']): PostTemplate[] => {
  return WALL_POST_TEMPLATES.filter((t) => t.category === category);
};
